import { Helmet } from 'react-helmet'
import React, { useEffect } from 'react';
import { useAuth } from '../../providers/auth_provider';
import axios from 'axios';

export default function Dashboard() {
    const { token } = useAuth();
    const [userData, setUsersData] = React.useState([]);
    const [frequenciesData, setFrequenciesData] = React.useState([]);
    const [categoriesData, setCategoriesData] = React.useState([]);
    const [affiliateStatistics, setAffiliateStatistics] = React.useState([]);
    const [subscriptionEarningsStatistics, setSubscriptionEarningsStatistics] = React.useState({});
    const [affiliatePayoutStatistics, setAffiliatePayoutStatistics] = React.useState({});
    const [deltaAmount, setDeltaAmount] = React.useState(0);
    const [loading, setLoading] = React.useState(true);


    // Create a single Axios instance
    const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
            Signature: process.env.REACT_APP_API_SIGNATURE,
        },
    });

    // Interceptor to add Authorization header dynamically
    api.interceptors.request.use(config => {
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    useEffect(() => {
        if (token) {
            fetchAllData();
        }
    }, [token]);

    // Function to fetch all data
    const fetchAllData = async () => {
        try {
            setLoading(true);
            const [
                userStatsResponse,
                frequenciesResponse,
                categoryResponse,
                affiliateStatsResponse,
                deltaAmountResponse,
                subscriptionEarningsResponse,
                affiliatePayoutResponse,
            ] = await Promise.all([
                api.get("/admin/get/users/statistics"),
                api.get("/frequency/all"),
                api.get("/category/all"),
                api.get("/admin/affiliate/statistics"),
                api.get("/admin/get/users/delta/amount/statistics"),
                api.get("/admin/earnings/subscriptions/statistics"),
                api.get("/admin/affiliate/payout/statistics"),
            ]);

            setUsersData(userStatsResponse.data);
            setFrequenciesData(frequenciesResponse.data.frequencies);
            setCategoriesData(categoryResponse.data.categories);
            setAffiliateStatistics(affiliateStatsResponse.data.data);
            setDeltaAmount(deltaAmountResponse.data.data);
            setSubscriptionEarningsStatistics(subscriptionEarningsResponse.data.earnings);
            setAffiliatePayoutStatistics(affiliatePayoutResponse.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Dashboard - Enervybe</title>
            </Helmet>

            {/* Card Border Shadow */}
            <div className="row g-6 mb-6">
                {/* total users */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-primary h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-primary">
                                        <i className="ri-group-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{userData?.total_users}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Users</h6>
                        </div>
                    </div>
                </div>
                {/* active users */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-info h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-info">
                                        <i className="ri-user-follow-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{userData?.active_users}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Active Users</h6>
                        </div>
                    </div>
                </div>
                {/* total frequencies */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-warning h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-warning">
                                        <i className="ri-disc-fill ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{frequenciesData?.length}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Frequencies</h6>
                        </div>
                    </div>
                </div>
                {/* total categories */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-danger h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-danger">
                                        <i className="ri-file-list-3-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{categoriesData?.length}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Categories</h6>
                        </div>
                    </div>
                </div>
            </div>

            {/* Admin Earnings by subscription Card  */}
            <div className="row g-6 mb-6">
                {/* weekly earnings */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-primary h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-primary">
                                        <i className="ri-calendar-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">${subscriptionEarningsStatistics?.weekly}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Current Week Subscription Earnings</h6>
                        </div>
                    </div>
                </div>
                {/* monthly earnings */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-info h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-info">
                                        <i className="ri-calendar-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">${subscriptionEarningsStatistics?.monthly}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Current Month Subscription Earnings</h6>
                        </div>
                    </div>
                </div>
                {/* previous month earnings */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-warning h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-warning">
                                        <i className="ri-arrow-left-down-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">${subscriptionEarningsStatistics?.previousMonth}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Previous Month Subscription Earnings</h6>
                        </div>
                    </div>
                </div>
                {/* yearly earnings */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-danger h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-danger">
                                        <i className="ri-calendar-check-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">${subscriptionEarningsStatistics?.yearly}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Yearly Subscription Earnings</h6>
                        </div>
                    </div>
                </div>
            </div>

            {/*affiliates statistics */}
            <div className="row g-6 mb-6">
                {/* total affiliates */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-primary h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-primary">
                                        <i className="ri-group-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{affiliateStatistics?.total_affiliates}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Affiliates</h6>
                        </div>
                    </div>
                </div>
                {/* total referrals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-danger h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-danger">
                                        <i className="ri-links-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{affiliateStatistics?.total_referrals}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Referrals</h6>
                        </div>
                    </div>
                </div>
                {/* total admin referrals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-info h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-info">
                                        <i className="ri-share-forward-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{affiliateStatistics?.total_admin_referrals}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Admin Referrals</h6>
                        </div>
                    </div>
                </div>
                {/* total affiliate (user) referrals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-warning h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-warning">
                                        <i className="ri-share-forward-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">{affiliateStatistics?.total_user_referrals}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Affiliate Referrals</h6>
                        </div>
                    </div>
                </div>
            </div>

            {/* Affiliate Payout */}
            <div className="row g-6 mb-6">

                {/* This Month Withdrawals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-success h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-success">
                                        <i className="ri-arrow-up-line ri-24px" />
                                    </span>
                                </div>
                                <div className='d-flex align-items-end'>
                                    <h4 className="mb-0">
                                        {/* {affiliatePayoutStatistics?.thisMonthTotalWithdrawals || 0}/ */}
                                        ${affiliatePayoutStatistics?.thisMonthTotalRequestedAmount || 0.00}
                                    </h4>
                                    {/* <span className="mb-1 fs-5 text-dark">
                                        ${affiliatePayoutStatistics?.thisMonthTotalRequestedAmount || 0.00}
                                    </span> */}
                                </div>
                            </div>
                            <h6 className="mb-0 fw-normal">This Month Total Requested Withdrawals</h6>
                        </div>
                    </div>
                </div>

                {/* Last Month Withdrawals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-warning h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-warning">
                                        <i className="ri-arrow-down-line ri-24px" />
                                    </span>
                                </div>
                                <div className='d-flex align-items-end'>
                                    <h4 className="mb-0">
                                        {/* {affiliatePayoutStatistics?.lastMonthTotalWithdrawals || 0}/ */}
                                        ${affiliatePayoutStatistics?.lastMonthTotalWithdrawalAmount || 0.00}
                                    </h4>
                                    {/* <span className="mb-1 fs-5 text-dark">
                                        ${affiliatePayoutStatistics?.lastMonthTotalWithdrawalAmount || 0.00}
                                    </span> */}
                                </div>
                            </div>
                            <h6 className="mb-0 fw-normal">Last Month Total Withdrawals</h6>
                        </div>
                    </div>
                </div>

                {/* Pending Withdrawals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-danger h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-danger">
                                        <i className="ri-arrow-down-line ri-24px" />
                                    </span>
                                </div>
                                <div className='d-flex align-items-end'>
                                    <h4 className="mb-0">
                                        {/* {affiliatePayoutStatistics?.totalPendingWithdrawals || 0}/ */}
                                        ${affiliatePayoutStatistics?.totalPendingWithdrawalAmount || 0.00}
                                    </h4>
                                    {/* <span className="mb-1 fs-5 text-dark">
                                        ${affiliatePayoutStatistics?.totalPendingWithdrawalAmount || 0.00}
                                    </span> */}
                                </div>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Pending Withdrawals</h6>
                        </div>
                    </div>
                </div>

                {/* Completed Withdrawals */}
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-success h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-success">
                                        <i className="ri-arrow-up-line ri-24px" />
                                    </span>
                                </div>
                                <div className='d-flex align-items-end'>
                                    <h4 className="mb-0">
                                        {/* {affiliatePayoutStatistics?.totalCompletedWithdrawals || 0}/ */}
                                        ${affiliatePayoutStatistics?.totalCompletedWithdrawalAmount || 0.00}
                                    </h4>
                                    {/* <span className="mb-1 fs-5 text-dark">
                                        ${affiliatePayoutStatistics?.totalCompletedWithdrawalAmount || 0.00}
                                    </span> */}
                                </div>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Completed Withdrawals</h6>
                        </div>
                    </div>
                </div>
            </div>

            {/* delta amount */}
            <div className="row g-6 mb-6">
                <div className="col-sm-6 col-lg-3">
                    <div className="card card-border-shadow-primary h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-2">
                                <div className="avatar me-4">
                                    <span className="avatar-initial rounded-3 bg-label-primary">
                                        <i className="ri-arrow-up-down-line ri-24px" />
                                    </span>
                                </div>
                                <h4 className="mb-0">${deltaAmount || 0}</h4>
                            </div>
                            <h6 className="mb-0 fw-normal">Total Users Delta Amount</h6>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
